import type { VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal } from "solid-js";
import { isEmpty, isNil } from "lodash-es";
import VersionHistoryHeader from "./VersionHistoryHeader.tsx";
import type { HistoryItem, HistoryItemAuthor } from "./sidebar/VersionHistorySidebar.tsx";
import VersionHistorySidebar from "./sidebar/VersionHistorySidebar.tsx";
import VersionHistoryPreview from "./VersionHistoryPreview.tsx";
import type { FormVersion } from "../../../state/formVersion.tsx";
import { useFormVersionState } from "../../../state/formVersion.tsx";
import { useFormState } from "../../../state/form.tsx";
import { useCustomerState } from "../../../state/customer.tsx";
import ApiService from "../../../services/ApiService.ts";
import type { User } from "../../../state/user.tsx";
import UserService from "../../../services/UserService.ts";
import { useInfiniteScrollWithQuery } from "../../../utils/useInfiniteScrollWithQuery.ts";
import { useModalManager } from "../../../components/modal/ModalManager.tsx";
import GenericFailureModal from "../../GenericFailureModal.tsx";
import type { MoreAppError } from "../../../MoreAppError.ts";
import { useTranslation } from "../../../state/i18n.tsx";
import FormVersionService from "../../../services/FormVersionService.ts";
import FormVersionId from "../../../state/FormVersionId.ts";
import type { ApiKey } from "../../../types/ApiKey.ts";
import { isValidObjectId } from "../../../utils/objectIdUtil.ts";

const HISTORY_ITEM_HEIGHT_ESTIMATE = 110;

const resolveAuthor = (
  lastUpdatedBy: string,
  usersByUsername: Record<string, User>,
  apiKeysById: Record<string, ApiKey>,
): HistoryItemAuthor | undefined => {
  const user = usersByUsername[lastUpdatedBy];
  if (!isNil(user)) {
    return { type: "user", user };
  }

  // When updated via API, `lastUpdatedBy` is the API key's ObjectId, not a username
  if (isValidObjectId(lastUpdatedBy)) {
    return { type: "apiUser", name: apiKeysById[lastUpdatedBy]?.name };
  }

  return undefined;
};

const VersionHistoryContent: VoidComponent = () => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const form = useFormState();
  const formVersion = useFormVersionState();
  const { open } = useModalManager();
  const [selectedFormVersion, setSelectedFormVersion] = createSignal<FormVersion | null>(null);
  const [scrollRef, setScrollRef] = createSignal<HTMLDivElement | undefined>();

  const apiKeysQuery = ApiService.getApiKeys();
  const apiKeysById = createMemo(() => {
    if (!apiKeysQuery.isSuccess) {
      return {};
    }
    return apiKeysQuery.data.reduce<Record<string, ApiKey>>((prev, curr) => {
      prev[curr.id.toString()] = curr;
      return prev;
    }, {});
  });

  const usersQuery = UserService.getUsers(customer.customerId);
  const usersByUsername = createMemo(() => {
    if (!usersQuery.isSuccess) {
      return {};
    }
    return usersQuery.data.reduce<Record<string, User>>((prev, curr) => {
      prev[curr.username] = curr;
      return prev;
    }, {});
  });
  const { query: formVersionsQuery, attemptToLoadMore } = useInfiniteScrollWithQuery(
    ApiService.getFormVersionsInfinite(form.id),
    scrollRef,
    2 * HISTORY_ITEM_HEIGHT_ESTIMATE,
  );

  const sidebarItems = createMemo((): HistoryItem[] => {
    if (!formVersionsQuery.isSuccess) {
      return [];
    }

    const allFormVersions = formVersionsQuery.data.pages.flat();
    const published = allFormVersions.filter((formVersion) => formVersion.meta.status === "FINAL");

    let items: HistoryItem[] = [];
    if (FormVersionService.hasUpdated()) {
      items.push({
        type: "unsavedChanges",
        formVersion: {
          ...formVersion,
          id: FormVersionId.from("unsaved"),
        },
        lastUpdatedAt: formVersion.lastUpdatedAt ?? new Date(),
      });
    }

    const firstFormVersion = allFormVersions[0];
    if (!isNil(firstFormVersion)) {
      items.push({
        type: "savedChanges",
        formVersion: {
          ...firstFormVersion,
          id: FormVersionId.from("saved"),
        },
        lastUpdatedAt: firstFormVersion.meta.lastUpdated,
      });
    }

    const publishedMapped = published.map((formVersion): HistoryItem => {
      const author = resolveAuthor(formVersion.meta.lastUpdatedBy, usersByUsername(), apiKeysById());
      const isCurrentlyPublished = form.publishedVersion?.formVersionId.equals(formVersion.id) ?? false;
      return {
        type: isCurrentlyPublished ? "currentlyPublished" : "previouslyPublished",
        formVersion,
        lastUpdatedAt: formVersion.meta.lastUpdated ?? new Date(0),
        author,
      };
    });
    return [...items, ...publishedMapped];
  });
  createEffect(() => {
    if (!isNil(selectedFormVersion())) {
      return;
    }

    const items = sidebarItems();
    if (!isEmpty(items)) {
      setSelectedFormVersion(items[0].formVersion);
    }
  });

  const error = createMemo(() => apiKeysQuery.error ?? usersQuery.error ?? formVersionsQuery.error);

  createEffect<MoreAppError | null>((previousError) => {
    const currentError = error();
    if (!isNil(currentError) && isNil(previousError)) {
      open({
        content: () => (
          <GenericFailureModal title={t(($) => $.VERSION_HISTORY_CONTENT_ERROR_MODAL_TITLE)} error={currentError} />
        ),
      });
    }
    return currentError;
  }, null);

  return (
    <div class="flex size-full flex-col">
      <VersionHistoryHeader />
      <div class="flex min-h-0 grow">
        <VersionHistorySidebar
          ref={(el: HTMLDivElement) => setScrollRef(el)}
          items={sidebarItems()}
          attemptToLoadMore={attemptToLoadMore}
          selectedFormVersion={selectedFormVersion()}
          setSelectedFormVersion={setSelectedFormVersion}
          isFetchingMore={formVersionsQuery.isFetching}
          hasReachedEnd={
            formVersionsQuery.isSuccess && !formVersionsQuery.hasNextPage && formVersionsQuery.data.pages.length > 1
          }
        />
        <VersionHistoryPreview form={form} formVersion={selectedFormVersion()} />
      </div>
    </div>
  );
};

export default VersionHistoryContent;
